import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import QRScanner from '@/components/QRScanner';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { CheckCircle, AlertCircle } from 'lucide-react';

interface ClientQRScannerProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  orderId?: string;
}

export const ClientQRScanner = ({ open, onClose, onSuccess, orderId }: ClientQRScannerProps) => {
  const { toast } = useToast();
  const [scanMode, setScanMode] = useState<'qr' | 'otp'>('qr');
  const [otpCode, setOtpCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [orderDetails, setOrderDetails] = useState<any>(null);

  useEffect(() => {
    if (open && orderId) {
      fetchOrderDetails();
    }
  }, [open, orderId]);

  const fetchOrderDetails = async () => {
    if (!orderId) {
      setOrderDetails(null);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('orders')
        .select(`
          *,
          products (title, images),
          client_profile:profiles!orders_client_id_fkey (full_name, phone)
        `)
        .eq('id', orderId)
        .single();

      if (error) throw error;
      setOrderDetails(data);
    } catch (error) {
      console.error('Error fetching order details:', error);
    }
  };

  const handleQRScan = async (scannedData: string) => {
    try {
      setLoading(true);
      
      console.log('[ClientQRScanner] Scanned data:', scannedData);
      
      // Le QR code client contient: ORDER:orderId:otp
      // Format attendu: ORDER:uuid:123456
      if (!scannedData.startsWith('ORDER:')) {
        toast({
          title: "QR Code invalide",
          description: "Format de QR code non reconnu",
          variant: "destructive"
        });
        return;
      }

      const parts = scannedData.split(':');
      if (parts.length !== 3) {
        toast({
          title: "QR Code invalide",
          description: "Format de QR code incorrect",
          variant: "destructive"
        });
        return;
      }

      const [, scannedOrderId, scannedOtpCode] = parts;
      
      console.log('[ClientQRScanner] Parsed:', { scannedOrderId, scannedOtpCode, expectedOrderId: orderId });

      // Valider et marquer comme récupéré
      await validatePickup(scannedOrderId, scannedOtpCode);
      
    } catch (error) {
      console.error('[ClientQRScanner] Error scanning QR:', error);
      toast({
        title: "Erreur",
        description: "Impossible de lire le QR code",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleOTPSubmit = async () => {
    if (otpCode.length !== 6) {
      toast({
        title: "Code invalide",
        description: "Le code OTP doit contenir 6 chiffres",
        variant: "destructive"
      });
      return;
    }

    try {
      setLoading(true);
      await validatePickup(orderId, otpCode);
    } catch (error) {
      console.error('Error validating OTP:', error);
      toast({
        title: "Erreur",
        description: "Impossible de valider le code OTP",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const validatePickup = async (orderIdToValidate: string, otpOrHash: string) => {
    try {
      let resolvedOrderDetails = orderDetails;

      if (!resolvedOrderDetails || resolvedOrderDetails.id !== orderIdToValidate) {
        const { data: fetchedOrderDetails, error: orderDetailsError } = await supabase
          .from('orders')
          .select(`
            *,
            products (title, images),
            client_profile:profiles!orders_client_id_fkey (full_name, phone)
          `)
          .eq('id', orderIdToValidate)
          .maybeSingle();

        if (orderDetailsError) {
          throw orderDetailsError;
        }

        resolvedOrderDetails = fetchedOrderDetails;
        setOrderDetails(fetchedOrderDetails);
      }

      const { data, error } = await supabase.functions.invoke('validate-pickup-token', {
        body: { 
          orderId: orderIdToValidate, 
          otpCode: otpOrHash 
        }
      });

      // Extract real message even if the function returned non-2xx
      if (error) {
        let msg = "Impossible de valider le retrait";
        try {
          const ctx = (error as any)?.context;
          if (ctx) {
            const body = typeof ctx === 'string' ? JSON.parse(ctx) : await ctx?.json?.();
            msg = body?.message || body?.error || msg;
          }
        } catch (_) {}
        toast({ title: "Erreur de validation", description: msg, variant: "destructive" });
        return;
      }

      if (data?.success) {
        toast({
          title: "Retrait confirmé !",
          description: `Commande récupérée par ${resolvedOrderDetails?.client_profile?.full_name || 'le client'}`
        });

        onSuccess();
        onClose();
      } else {
        toast({
          title: "Code invalide",
          description: data?.message || data?.error || "Le code de retrait n'est pas valide",
          variant: "destructive"
        });
      }
    } catch (error: any) {
      console.error('Error validating pickup:', error);
      toast({
        title: "Erreur de validation",
        description: error.message || "Impossible de valider le retrait",
        variant: "destructive"
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="w-[95vw] max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Confirmer le retrait client</DialogTitle>
          <DialogDescription>
            Scanner le QR code du client ou saisir le code OTP
          </DialogDescription>
        </DialogHeader>

        {orderDetails && (
          <div className="bg-muted p-3 sm:p-4 rounded-lg mb-4">
            <p className="text-sm font-medium">Commande à remettre :</p>
            <p className="text-base sm:text-lg font-semibold">{orderDetails.products?.title}</p>
            <p className="text-sm text-muted-foreground">
              Client : {orderDetails.client_profile?.full_name || 'Non renseigné'}
            </p>
            <p className="text-xs text-muted-foreground">
              N° commande : {orderDetails.order_number}
            </p>
          </div>
        )}

        <Tabs value={scanMode} onValueChange={(value) => setScanMode(value as 'qr' | 'otp')}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="qr">Scanner QR</TabsTrigger>
            <TabsTrigger value="otp">Code OTP</TabsTrigger>
          </TabsList>

          <TabsContent value="qr" className="space-y-4">
            <div className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-4 sm:p-8">
              <QRScanner
                onScanSuccess={(scannedOrderId, scannedOtp) => {
                  setLoading(true);
                  validatePickup(scannedOrderId, scannedOtp).finally(() => setLoading(false));
                }}
                onClose={onClose}
              />
            </div>
            <div className="flex items-start gap-2 text-sm text-muted-foreground">
              <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <p>
                Demandez au client de présenter le QR code reçu par email ou disponible dans son espace commandes
              </p>
            </div>
          </TabsContent>

          <TabsContent value="otp" className="space-y-4">
            <div>
              <Label htmlFor="otpCode">Code OTP (6 chiffres)</Label>
              <Input
                id="otpCode"
                placeholder="123456"
                value={otpCode}
                onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ''))}
                maxLength={6}
                className="text-center text-xl sm:text-2xl tracking-widest font-mono"
                disabled={loading}
              />
              <p className="text-xs text-muted-foreground mt-2">
                Demandez au client de vous communiquer son code OTP à 6 chiffres
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-2">
              <Button variant="outline" className="flex-1" onClick={onClose} disabled={loading}>
                Annuler
              </Button>
              <Button 
                className="flex-1" 
                onClick={handleOTPSubmit}
                disabled={loading || otpCode.length !== 6}
              >
                <CheckCircle className="h-4 w-4 mr-2" />
                {loading ? 'Validation...' : 'Confirmer le retrait'}
              </Button>
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};
